import { Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { FormattedMessage } from 'react-intl';

import {
  ApiWorkflowDialog,
  DialogButtons,
  DialogContent,
  DialogMessageBox,
  DialogTitle,
} from '@verantos-dev/shared-components';
import { CohortDefinitionStatus } from '@verantos-dev/types-cohorts';

import { useSaveCohortDefinitionModal } from './useSaveCohortDefinitionModal';
import { useSaveCohortDefinitionModalString } from './useSaveCohortDefinitionModalString';
import { AutoSubmitDialogWrapper } from '../AutoSubmitDialogWrapper/AutoSubmitDialogWrapper';

export const SaveCohortDefinitionModal = () => {
  const {
    intl,
    title,
    cohortData,
    onClose,
    onSubmit,
    countDown,
    submittingError,
    isSubmitting,
    isInitialLoading,
    initialLoadingError,
    isSubmitSuccess,
  } = useSaveCohortDefinitionModal();

  const { apiWorkflowDialogMessages, dialogContentMessages } = useSaveCohortDefinitionModalString({
    cohortName: cohortData?.cohortName,
  });

  if (cohortData?.latestStatus === CohortDefinitionStatus.PROCESSED) {
    return (
      <ApiWorkflowDialog
        id="save-cohort-definition-modal"
        title={title}
        isInitialLoading={isInitialLoading}
        initialLoadingError={initialLoadingError ?? undefined}
        isSubmitting={isSubmitting}
        submittingError={submittingError ?? undefined}
        onSubmit={onSubmit}
        onClose={onClose}
        dialogMessages={apiWorkflowDialogMessages}
        hideDisplayButtonsOnSuccess
      >
        <DialogMessageBox
          id="confirm-cohort-save-message-box"
          title={dialogContentMessages.title}
          type="info"
          renderSubmitted={
            <Stack gap={4}>
              <div>{dialogContentMessages.message1}</div>
              <div>
                <FormattedMessage
                  id="projects.cohortBuilderPage.saveCohortDefinitionModal.message2"
                  values={{
                    processed: (
                      <strong style={{ fontWeight: 700 }}>{dialogContentMessages.processed}</strong>
                    ),
                    draft: (
                      <strong style={{ fontWeight: 700 }}>{dialogContentMessages.draft}</strong>
                    ),
                  }}
                />
              </div>
            </Stack>
          }
        />
      </ApiWorkflowDialog>
    );
  }

  if (submittingError) {
    return (
      <AutoSubmitDialogWrapper id="save-cohort-definition-modal" onClose={onClose}>
        <DialogTitle id="cohort-builder-save-modal-title" showClose={true} onClose={onClose}>
          {title}
        </DialogTitle>
        <DialogContent>
          <DialogMessageBox
            id="cohort-builder-save-modal-error"
            title={intl.formatMessage({
              id: 'projects.cohortBuilderPage.saveCohortDefinitionModal.errorTitle',
            })}
            type="error"
            text={intl.formatMessage({
              id: 'projects.cohortBuilderPage.saveCohortDefinitionModal.errorMessage',
            })}
          />
        </DialogContent>
        <DialogButtons
          id="cohort-builder-save-modal-confirmation-buttons"
          saveButton={{
            label: intl.formatMessage({
              id: 'projects.cohortBuilderPage.saveCohortDefinitionModal.retryBtn',
            }),
            onClick: onSubmit,
          }}
        />
      </AutoSubmitDialogWrapper>
    );
  }

  if (isSubmitSuccess) {
    return (
      <AutoSubmitDialogWrapper id="save-cohort-definition-modal" onClose={onClose}>
        <DialogTitle id="cohort-builder-save-modal-title" showClose={true} onClose={onClose}>
          {title}
        </DialogTitle>
        <DialogContent>
          <Stack gap={4}>
            <DialogMessageBox
              id="cohort-builder-save-modal-success"
              title={intl.formatMessage({
                id: 'projects.cohortBuilderPage.saveCohortDefinitionModal.successTitle',
              })}
              type="success"
              text={intl.formatMessage(
                {
                  id: 'projects.cohortBuilderPage.saveCohortDefinitionModal.successText',
                },
                {
                  cohortName: cohortData?.cohortName || 'Cohort',
                }
              )}
            />
            <Typography variant="body2" style={{ fontSize: '12px' }}>
              {intl.formatMessage(
                {
                  id: 'projects.cohortBuilderPage.saveCohortDefinitionModal.successMessageText',
                },
                {
                  countDown,
                }
              )}
            </Typography>
          </Stack>
        </DialogContent>
      </AutoSubmitDialogWrapper>
    );
  }

  return (
    <AutoSubmitDialogWrapper
      id="save-cohort-definition-modal"
      onClose={() => {
        // do nothing
      }}
    >
      <DialogTitle id="cohort-builder-save-modal-title" showClose={false}>
        {title}
      </DialogTitle>
      <DialogContent>
        <DialogMessageBox
          id="cohort-builder-save-modal-loading"
          title={intl.formatMessage({
            id: 'projects.cohortBuilderPage.saveCohortDefinitionModal.savingTitle',
          })}
          type="loading"
          text={intl.formatMessage({
            id: 'projects.cohortBuilderPage.saveCohortDefinitionModal.description4',
          })}
        />
      </DialogContent>
    </AutoSubmitDialogWrapper>
  );
};
